import './TechApps.css';
import { useState, useEffect, useContext } from 'react';
import FieldServiceAPI from '../Services/FieldService';
import { Context } from '../Context';

const myAppointmentsHelpLink = 'https://cornerstonedetentionproducts.box.com/s/slhnud8q46liwgvlbn45csdrppm9mani';

const fetchAppointments = async (apiToken, userEmail, setAppointments, setError) => {
    try {
        const response = await FieldServiceAPI.getAppointmentsByUserEmail(apiToken, userEmail);
        if (response.status > 199 && response.status < 300) setAppointments(response.data);
        else setError(`Error fetching appointments for ${userEmail}`);
    } catch (error) {
        console.error(error);
        setError(`Error fetching appointments for ${userEmail}`);
    };
};

const fetchHistoricAppointments = async (apiToken, userEmail, filterObject, setHistoricAppointments, setError) => {
    try {
        filterObject.techEmail = userEmail;
        const response = await FieldServiceAPI.getHistoricAppointments(apiToken, filterObject);
        if (response.status > 199 && response.status < 300) setHistoricAppointments(response.data);
        else setError(`Error fetching historic appointments for ${userEmail}`);
    } catch (error) {
        console.error(error);
        setError(`Error fetching historic appointments for ${userEmail}`);
    };
};

const fetchCustomerAndLocations = async (apiToken, setCustomerAndLocations, setError) => {
    try {
        const response = await FieldServiceAPI.getCustomersAndLocations(apiToken);
        if (response.status > 199 && response.status < 300) console.log(response.data);
        else setError('Error fetching customers and locations');
    } catch (error) {
        console.error(error);
        setError('Error fetching customers and locations');
    };
};

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time for comparison
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
    
    if (dateOnly.getTime() === todayOnly.getTime()) {
        return 'Today';
    } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
        return 'Tomorrow';
    } else {
        return date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'short', 
            day: 'numeric' 
        });
    }
};

const formatTime = (timeString) => {
    const time = new Date(timeString);
    return time.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
    });
};

const expandMultiDayAppointments = (appointments, useActualDates = false) => {
    const expandedAppointments = [];
    
    appointments.forEach(appointment => {
        const startDateField = useActualDates ? appointment.actualStart : appointment.scheduledStart;
        const endDateField = useActualDates ? appointment.actualEnd : appointment.scheduledEnd;
        const startDate = new Date(startDateField);
        const endDate = new Date(endDateField);
        
        // Reset time to compare dates only
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        
        // If appointment spans multiple days, create a copy for each day
        if (startDateOnly.getTime() !== endDateOnly.getTime()) {
            const currentDate = new Date(startDateOnly);
            
            while (currentDate <= endDateOnly) {
                // Skip weekends (Saturday = 6, Sunday = 0)
                const dayOfWeek = currentDate.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                
                if (!isWeekend) {
                    const duplicatedAppointment = {
                        ...appointment,
                        // Create unique key for React by combining appointmentID with date
                        appointmentKey: `${appointment.appointmentID}-${currentDate.toISOString().split('T')[0]}`,
                        // Adjust start and end dates for this specific day
                        daySpecificStartDate: new Date(currentDate),
                        daySpecificEndDate: new Date(currentDate),
                        // Mark as multi-day for potential visual distinction
                        isMultiDay: true,
                        originalStartDate: startDateField,
                        originalEndDate: endDateField
                    };
                    
                    // For the first day, keep original start time
                    if (currentDate.getTime() === startDateOnly.getTime()) {
                        duplicatedAppointment.daySpecificStartDate = new Date(startDateField);
                    } else {
                        // For subsequent days, start at beginning of day
                        duplicatedAppointment.daySpecificStartDate.setHours(0, 0, 0, 0);
                    }
                    
                    // For the last day, keep original end time
                    if (currentDate.getTime() === endDateOnly.getTime()) {
                        duplicatedAppointment.daySpecificEndDate = new Date(endDateField);
                    } else {
                        // For preceding days, end at end of day
                        duplicatedAppointment.daySpecificEndDate.setHours(23, 59, 59, 999);
                    }
                    
                    expandedAppointments.push(duplicatedAppointment);
                }
                
                // Move to next day
                currentDate.setDate(currentDate.getDate() + 1);
            }
        } else {
            // Single day appointment, add as-is with original dates
            expandedAppointments.push({
                ...appointment,
                appointmentKey: appointment.appointmentID,
                isMultiDay: false,
                daySpecificStartDate: new Date(startDateField),
                daySpecificEndDate: new Date(endDateField)
            });
        }
    });
    
    return expandedAppointments;
};

const groupAppointmentsByDate = (appointments) => {
    const grouped = {};
    
    appointments.forEach(appointment => {
        const dateKey = formatDate(appointment.daySpecificStartDate);
        if (!grouped[dateKey]) {
            grouped[dateKey] = [];
        }
        grouped[dateKey].push(appointment);
    });
    
    // Sort appointments within each group by start time
    Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => new Date(a.daySpecificStartDate) - new Date(b.daySpecificStartDate));
    });
    
    return grouped;
};

const AppointmentCard = ({ appointment, showCompletedDate = false, onClick }) => {
    const normalizedStatus = appointment?.status == null ? 'Not Started' : appointment.status;
    const statusClassName = normalizedStatus.toLowerCase().replace(/\s+/g, '-');

    const handleClick = () => {
        if (onClick) {
            onClick(appointment);
        } else {
            const url = appointment.appointmentLink || `https://cornerstoneinc.io/AppointmentViewer/${appointment.id}`;
            window.open(url, '_blank');
        }
    };
    
    const formatCompletedDate = (dateString) => {
        if (!dateString) return 'Not completed';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getTimeDisplay = () => {
        // For historic appointments without daySpecificDates, use actual times
        const startDate = appointment.daySpecificStartDate || appointment.actualStart || appointment.scheduledStart;
        const endDate = appointment.daySpecificEndDate || appointment.actualEnd || appointment.scheduledEnd;
        
        if (!startDate || !endDate) return 'Time not available';
        
        const startTime = formatTime(startDate);
        const endTime = formatTime(endDate);
        
        if (appointment.isMultiDay) {
            const originalStartDate = new Date(appointment.originalStartDate);
            const originalEndDate = new Date(appointment.originalEndDate);
            const currentDayDate = new Date(appointment.daySpecificStartDate);
            
            // Reset times for date comparison
            const currentDayOnly = new Date(currentDayDate.getFullYear(), currentDayDate.getMonth(), currentDayDate.getDate());
            const originalStartOnly = new Date(originalStartDate.getFullYear(), originalStartDate.getMonth(), originalStartDate.getDate());
            const originalEndOnly = new Date(originalEndDate.getFullYear(), originalEndDate.getMonth(), originalEndDate.getDate());
            
            // First day of multi-day appointment
            if (currentDayOnly.getTime() === originalStartOnly.getTime()) {
                // Only show start time if it doesn't end on this day
                if (currentDayOnly.getTime() !== originalEndOnly.getTime()) {
                    return `${startTime} - Continues`;
                }
                // Single day that happens to use multi-day processing
                return `${startTime} - ${endTime}`;
            }
            // Last day of multi-day appointment  
            else if (currentDayOnly.getTime() === originalEndOnly.getTime()) {
                return `Continues - ${endTime}`;
            }
            // Middle days of multi-day appointment
            else {
                return "All Day";
            }
        }
        
        return `${startTime} - ${endTime}`;
    };

    return (
        <div className={`appointment-card status-${statusClassName}`} onClick={handleClick}>
            <div className="tech-app-appointment-header">
                <div className="appointment-time">
                    {getTimeDisplay()}
                    {appointment.isMultiDay && (
                        <span className="multi-day-indicator">📅 Multi-day</span>
                    )}
                </div>
                <div className="appointment-type">{appointment.type}</div>
            </div>
            <div className="appointment-customer">
                {appointment.customer?.customerName || 'No Customer'}
            </div>
            <div className="appointment-location">
                {appointment.location?.locationName || appointment.location?.locationID || 'No Location'}
            </div>
            <div className="appointment-descriptions">
                {appointment.description || 'No description provided'}
            </div>
            {showCompletedDate && (
                <div className="appointment-completed-date">
                    Completed: {formatCompletedDate(appointment.completedDate)}
                </div>
            )}
            {!showCompletedDate && (
                <div className="appointment-status">
                    <span className={`status-badge ${statusClassName}`}>
                        {normalizedStatus}
                    </span>
                </div>
            )}
        </div>
    );
};

const AppointmentModal = ({ appointment, onClose }) => {
    if (!appointment) return null;
    
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };
    
    const formatDuration = (hours, minutes) => {
        if (!hours && !minutes) return 'N/A';
        const parts = [];
        if (hours) parts.push(`${hours}h`);
        if (minutes) parts.push(`${minutes}m`);
        return parts.join(' ');
    };
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Appointment Details</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                
                <div className="modal-body">
                    <div className="modal-section">
                        <h3>Basic Information</h3>
                        <div className="modal-field">
                            <span className="field-label">Appointment #:</span>
                            <span className="field-value">{appointment.appointmentNbr}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Type:</span>
                            <span className="field-value">{appointment.type}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Status:</span>
                            <span className="field-value">{appointment.status == null ? 'Not Started' : appointment.status}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Branch:</span>
                            <span className="field-value">{appointment.branch}</span>
                        </div>
                    </div>
                    
                    <div className="modal-section">
                        <h3>Customer & Location</h3>
                        <div className="modal-field">
                            <span className="field-label">Customer:</span>
                            <span className="field-value">{appointment.customer?.customerName}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Customer #:</span>
                            <span className="field-value">{appointment.customer?.customerNumber}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Location:</span>
                            <span className="field-value">{appointment.location?.locationName}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Address:</span>
                            <span className="field-value">
                                {appointment.location?.address && (
                                    <>
                                        {appointment.location.address.street}
                                        {appointment.location.address.street2 && `, ${appointment.location.address.street2}`}
                                        <br />
                                        {appointment.location.address.city}, {appointment.location.address.state} {appointment.location.address.zip}
                                    </>
                                )}
                            </span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Contact:</span>
                            <span className="field-value">
                                {appointment.contacts?.name}<br />
                                {appointment.contacts?.email}<br />
                                {appointment.contacts?.title}
                            </span>
                        </div>
                    </div>
                    
                    <div className="modal-section">
                        <h3>Timing</h3>
                        <div className="modal-field">
                            <span className="field-label">Scheduled Start:</span>
                            <span className="field-value">{formatDateTime(appointment.scheduledStart)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Scheduled End:</span>
                            <span className="field-value">{formatDateTime(appointment.scheduledEnd)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Actual Start:</span>
                            <span className="field-value">{formatDateTime(appointment.actualStart)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Actual End:</span>
                            <span className="field-value">{formatDateTime(appointment.actualEnd)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Completed:</span>
                            <span className="field-value">{formatDateTime(appointment.completedDate)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Travel Time:</span>
                            <span className="field-value">{formatDuration(appointment.travelHours, appointment.travelMinutes)}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Onsite Time:</span>
                            <span className="field-value">{formatDuration(appointment.onsiteHours, appointment.onsiteMinutes)}</span>
                        </div>
                    </div>
                    
                    <div className="modal-section">
                        <h3>Technicians</h3>
                        {appointment.technicians?.map((tech, index) => (
                            <div key={index} className="modal-field">
                                <span className="field-value">
                                    {tech.name}<br />
                                    <small>{tech.email}</small>
                                </span>
                            </div>
                        ))}
                    </div>
                    
                    <div className="modal-section">
                        <h3>Description & Notes</h3>
                        <div className="modal-field">
                            <span className="field-label">Description:</span>
                            <span className="field-value">{appointment.description || 'N/A'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Office Notes:</span>
                            <span className="field-value">{appointment.officeNotes || 'N/A'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Tech Notes:</span>
                            <span className="field-value">{appointment.techNotes || 'N/A'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Trouble Description:</span>
                            <span className="field-value">{appointment.troubleDescription || 'N/A'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Repair Description:</span>
                            <span className="field-value">{appointment.repairDescription || 'N/A'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Additional Comments:</span>
                            <span className="field-value">{appointment.additionalComments || 'N/A'}</span>
                        </div>
                    </div>
                    
                    <div className="modal-section">
                        <h3>Documentation</h3>
                        <div className="modal-field">
                            <span className="field-label">FSR Complete:</span>
                            <span className="field-value">{appointment.fsrComplete ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">FSR Sent:</span>
                            <span className="field-value">{appointment.fsrSent ? 'Yes' : 'No'}</span>
                        </div>
                        {appointment.fsrSentDate && (
                            <div className="modal-field">
                                <span className="field-label">FSR Sent Date:</span>
                                <span className="field-value">{formatDateTime(appointment.fsrSentDate)}</span>
                            </div>
                        )}
                        {appointment.fsrPath && (
                            <div className="modal-field">
                                <span className="field-label">FSR Path:</span>
                                <span className="field-value">
                                    <a href={appointment.fsrPath} target="_blank" rel="noopener noreferrer">
                                        View FSR
                                    </a>
                                </span>
                            </div>
                        )}
                        {appointment.fileShareLink && (
                            <div className="modal-field">
                                <span className="field-label">File Share:</span>
                                <span className="field-value">
                                    <a href={appointment.fileShareLink} target="_blank" rel="noopener noreferrer">
                                        View Files
                                    </a>
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {appointment.projectNumber && (
                        <div className="modal-section">
                            <h3>Project Information</h3>
                            <div className="modal-field">
                                <span className="field-label">Project Number:</span>
                                <span className="field-value">{appointment.projectNumber}</span>
                            </div>
                        </div>
                    )}
                    
                    <div className="modal-section">
                        <h3>System Information</h3>
                        <div className="modal-field">
                            <span className="field-label">Created By:</span>
                            <span className="field-value">{appointment.createdBy}</span>
                        </div>
                        <div className="modal-field">
                            <span className="field-label">Created Date:</span>
                            <span className="field-value">{formatDateTime(appointment.createdDate)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TechApps = () => {
    const contextObject = useContext(Context);
    const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming' or 'historic'
    const [appointments, setAppointments] = useState([]);
    const [historicAppointments, setHistoricAppointments] = useState([]);
    const [customersAndLocations, setCustomersAndLocations] = useState([]);
    const [appointmentsError, setAppointmentsError] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Filter states for historic appointments
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [availableLocations, setAvailableLocations] = useState([]);
    
    useEffect(() => {
        if (contextObject && contextObject.activeUser && contextObject.activeUser.email) {
            if (appointments.length < 1) {
                fetchAppointments(contextObject.apiToken, contextObject.activeUser.email, setAppointments, setAppointmentsError);
            }
            if (customersAndLocations.length < 1) {
                console.log('Fetching customers and locations...');
                fetchCustomerAndLocations(contextObject.apiToken, setCustomersAndLocations, setAppointmentsError);
            }
        };
    }, [contextObject]);

    useEffect(() => {
        if(appointments) console.log('Upcoming:', appointments);
    }, [appointments]);
    
    useEffect(() => {
        if(historicAppointments) console.log('Historic:', historicAppointments);
    }, [historicAppointments]);
    
    // Update available locations when customer is selected
    useEffect(() => {
        if (selectedCustomer && customersAndLocations.length > 0) {
            const customer = customersAndLocations.find(c => c.id === parseInt(selectedCustomer));
            setAvailableLocations(customer?.locations || []);
            setSelectedLocation(''); // Reset location when customer changes
        } else {
            setAvailableLocations([]);
            setSelectedLocation('');
        }
    }, [selectedCustomer, customersAndLocations]);
    
    // Fetch historic appointments when filters change
    useEffect(() => {
        if (viewMode === 'historic' && contextObject?.apiToken && contextObject?.activeUser?.email) {
            const filterObject = {};
            if (selectedCustomer) filterObject.customer = parseInt(selectedCustomer);
            if (selectedLocation) filterObject.location = parseInt(selectedLocation);
            if (fromDate) filterObject.fromDate = fromDate;
            if (toDate) filterObject.toDate = toDate;
            
            fetchHistoricAppointments(
                contextObject.apiToken,
                contextObject.activeUser.email,
                filterObject,
                setHistoricAppointments,
                setAppointmentsError
            );
        }
    }, [viewMode, selectedCustomer, selectedLocation, fromDate, toDate, contextObject]);

    // Prepare appointments based on view mode
    let orderedGroups = [];
    let sortedHistoricAppointments = [];
    
    if (viewMode === 'upcoming') {
        // Expand multi-day appointments, then sort and filter future appointments
        const expandedAppointments = expandMultiDayAppointments(appointments, false);
        const sortedAppointments = expandedAppointments
            .filter(appointment => new Date(appointment.daySpecificStartDate) >= new Date().setHours(0, 0, 0, 0))
            .sort((a, b) => new Date(a.daySpecificStartDate) - new Date(b.daySpecificStartDate));

        const groupedAppointments = groupAppointmentsByDate(sortedAppointments);
        
        // Order the groups: Today, Tomorrow, then chronologically
        if (groupedAppointments['Today']) orderedGroups.push(['Today', groupedAppointments['Today']]);
        if (groupedAppointments['Tomorrow']) orderedGroups.push(['Tomorrow', groupedAppointments['Tomorrow']]);
        
        Object.entries(groupedAppointments)
            .filter(([key]) => key !== 'Today' && key !== 'Tomorrow')
            .sort(([,a], [,b]) => new Date(a[0].daySpecificStartDate) - new Date(b[0].daySpecificStartDate))
            .forEach(group => orderedGroups.push(group));
    } else {
        // Historic appointments - no date grouping, just flat list sorted by completed date (most recent first)
        sortedHistoricAppointments = historicAppointments
            .map(appointment => ({
                ...appointment,
                appointmentKey: appointment.appointmentID
            }))
            .sort((a, b) => {
                const dateA = a.completedDate ? new Date(a.completedDate) : new Date(a.actualEnd || a.scheduledEnd);
                const dateB = b.completedDate ? new Date(b.completedDate) : new Date(b.actualEnd || b.scheduledEnd);
                return dateB - dateA;
            });
    }

    const handleClearFilters = () => {
        setSelectedCustomer('');
        setSelectedLocation('');
        setFromDate('');
        setToDate('');
    };
    
    const handleAppointmentClick = (appointment) => {
        setSelectedAppointment(appointment);
        setIsModalOpen(true);
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAppointment(null);
    };

    const handleOpenHelp = () => {
        if (!myAppointmentsHelpLink) return;
        window.open(myAppointmentsHelpLink, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="tech-apps">
            <div className="appointments-header">
                <div className="view-toggle-row">
                    <button 
                        className={`toggle-button ${viewMode === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setViewMode('upcoming')}
                    >
                        Upcoming
                    </button>
                    <button 
                        className={`toggle-button ${viewMode === 'historic' ? 'active' : ''}`}
                        onClick={() => setViewMode('historic')}
                    >
                        Past
                    </button>
                    <button
                        className="help-icon-button"
                        onClick={handleOpenHelp}
                        disabled={!myAppointmentsHelpLink}
                        title={myAppointmentsHelpLink ? 'Open help documentation' : 'Help link not configured'}
                        aria-label="Open help documentation"
                    >
                        ?
                    </button>
                </div>
                {appointmentsError && (
                    <div className="error-message">{appointmentsError}</div>
                )}
                
                {viewMode === 'historic' && (
                    <>
                        <div 
                            className="filters-toggle-bar" 
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                        >
                            <span className="filters-toggle-text">Filters</span>
                            <span className={`filters-toggle-arrow ${filtersExpanded ? 'expanded' : ''}`}>
                                ▲
                            </span>
                        </div>
                        
                        {filtersExpanded && (
                            <div className="filters-container">
                        <div className="filter-group">
                            <label htmlFor="customer-select">Customer:</label>
                            <select 
                                id="customer-select"
                                value={selectedCustomer} 
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Customers</option>
                                {customersAndLocations.map(customer => (
                                    <option key={customer.id} value={customer.id}>
                                        {customer.customerName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label htmlFor="location-select">Location:</label>
                            <select 
                                id="location-select"
                                value={selectedLocation} 
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                className="filter-select"
                                disabled={!selectedCustomer}
                            >
                                <option value="">All Locations</option>
                                {availableLocations.map(location => (
                                    <option key={location.locationID} value={location.locationID}>
                                        {location.locationName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label htmlFor="from-date">From Date:</label>
                            <input 
                                id="from-date"
                                type="date" 
                                value={fromDate} 
                                onChange={(e) => setFromDate(e.target.value)}
                                className="filter-input"
                            />
                        </div>
                        
                        <div className="filter-group">
                            <label htmlFor="to-date">To Date:</label>
                            <input 
                                id="to-date"
                                type="date" 
                                value={toDate} 
                                onChange={(e) => setToDate(e.target.value)}
                                className="filter-input"
                            />
                        </div>
                        
                                <button onClick={handleClearFilters} className="clear-filters-button">
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            
            <div className="appointments-container">
                {viewMode === 'upcoming' ? (
                    orderedGroups.length === 0 ? (
                        <div className="no-appointments">
                            <div className="no-appointments-icon">📅</div>
                            <h3>No upcoming appointments</h3>
                            <p>You're all caught up!</p>
                        </div>
                    ) : (
                        orderedGroups.map(([dateGroup, appointmentList]) => (
                            <div key={dateGroup} className="date-group">
                                <h2 className="date-header">{dateGroup}</h2>
                                <div className="appointments-list">
                                    {appointmentList.map((appointment) => (
                                        <AppointmentCard 
                                            key={appointment.appointmentKey} 
                                            appointment={appointment}
                                            showCompletedDate={false}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    )
                ) : (
                    sortedHistoricAppointments.length === 0 ? (
                        <div className="no-appointments">
                            <div className="no-appointments-icon">📅</div>
                            <h3>No past appointments found</h3>
                            <p>Try adjusting your filters</p>
                        </div>
                    ) : (
                        <div className="historic-appointments-list">
                            {sortedHistoricAppointments.map((appointment) => (
                                <AppointmentCard 
                                    key={appointment.appointmentKey} 
                                    appointment={appointment}
                                    showCompletedDate={true}
                                    onClick={handleAppointmentClick}
                                />
                            ))}
                        </div>
                    )
                )}
            </div>
            
            {isModalOpen && (
                <AppointmentModal 
                    appointment={selectedAppointment} 
                    onClose={handleCloseModal} 
                />
            )}
        </div>
    )
};

export default TechApps;